import autoTable from "jspdf-autotable";
import { formatLocalDateOnly } from "../../shared utils/dateFormatters.js";
import {
  jsPDF,
  houseTableDefaults,
  brandAccentLabel,
  brandAccentText,
  drawLetterhead,
  drawFooter,
} from "../../shared utils/pdfHouseStyle.js";

// Single source of truth for the branded invoice/receipt PDF, shared by the
// student Invoice tab and the agent module's per-stay invoice so the two are
// pixel-identical. Commission is NEVER named on the page: gross vs net only
// changes the numbers (net silently reduces commissionable lines by `factor`).
// All totals are derived here from (bookings, payments, gross, factor) so both
// callers produce the same maths.

const DISCOUNT_PREFIX = "DISCOUNT:";
const isDiscountPayment = (p) =>
  (p?.paymentaccount || "").toUpperCase().startsWith(DISCOUNT_PREFIX);

export function generateStudentInvoicePdf({
  invoiceDate,
  headerNote = "",
  student = {},
  selectedStay = null,
  bookings = [],
  payments = [],
  gross = true,
  factor = 1,
}) {
  const sortedBookings = [...bookings].sort(
    (a, b) => new Date(a.startDate) - new Date(b.startDate)
  );

  const totalGrossCharge = bookings.reduce((acc, b) => acc + b.actualCharge, 0);
  const totalNetCharge = bookings.reduce(
    (acc, b) => acc + (b.commissionable ? b.actualCharge * factor : b.actualCharge),
    0
  );
  const discountTotal = payments
    .filter(isDiscountPayment)
    .reduce((acc, p) => acc + (Number(p.paymentamount) || 0), 0);
  const invoiceTotal = gross ? totalGrossCharge : totalNetCharge;
  const totalAfterDiscounts = Math.max(0, invoiceTotal - discountTotal);
  const depositDue = Math.max(200, 0.1 * totalAfterDiscounts);
  const paidTotalExcludingDiscounts = payments
    .filter((p) => !isDiscountPayment(p))
    .reduce((acc, p) => acc + (Number(p.paymentamount) || 0), 0);
  const totalAlreadyPaid = paidTotalExcludingDiscounts + discountTotal;
  const outstandingBalanceGross = totalGrossCharge - totalAlreadyPaid;
  const outstandingBalanceNet = totalNetCharge - totalAlreadyPaid;

  const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
  const pageWidth = pdf.internal.pageSize.getWidth();

  const formattedPdfDate =
    invoiceDate && invoiceDate.format ? invoiceDate.format("DD-MM-YYYY") : invoiceDate;

  const checkInDate = selectedStay?.arrivalDate
    ? formatLocalDateOnly(new Date(selectedStay.arrivalDate))
    : "Not available";
  const checkOutDate = selectedStay?.departureDate
    ? formatLocalDateOnly(new Date(selectedStay.departureDate))
    : "Not available";

  // Header — Invoice when nothing real has been paid, Receipt once it has.
  let y = drawLetterhead(pdf, {
    title: paidTotalExcludingDiscounts === 0 ? "Invoice" : "Receipt",
  });

  pdf.setFontSize(11);
  brandAccentLabel(pdf, "Student ID", student.mtRef || "", 40, y);
  brandAccentLabel(pdf, "Date", formattedPdfDate, pageWidth - 120, y);

  y += 25;

  // Optional free-text header block (e.g. parent name/address, VAT number)
  const noteText = (headerNote || "").trim();
  if (noteText) {
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(10);
    pdf.setTextColor(40);
    const noteLines = pdf.splitTextToSize(noteText, pageWidth - 80);
    pdf.text(noteLines, 40, y);
    y += noteLines.length * 13 + 12;
  }

  autoTable(pdf, {
    ...houseTableDefaults,
    startY: y,
    head: [[{ content: "Invoice Details", colSpan: 4, styles: { halign: "left" } }]],
    body: [
      ["First Name", student.studentName || "", "Surname", student.studentSurname || ""],
      ["Invoice Number", `${student.mtRef || ""}-01`, "", ""],
      ["Check In", checkInDate, "Check Out", checkOutDate],
    ],
  });

  y = pdf.lastAutoTable.finalY + 18;

  brandAccentText(pdf, "Booked Items", pageWidth / 2, y, 12);
  y += 8;

  autoTable(pdf, {
    ...houseTableDefaults,
    startY: y,
    head: [["Product", "Start", "End", "Amount"]],
    body:
      sortedBookings.length > 0
        ? sortedBookings.map((booking) => [
            booking.productName || "",
            booking.startDate ? formatLocalDateOnly(new Date(booking.startDate)) : "",
            booking.endDate ? formatLocalDateOnly(new Date(booking.endDate)) : "",
            `£ ${
              booking.commissionable && !gross
                ? (booking.actualCharge * factor).toFixed(2)
                : booking.actualCharge.toFixed(2)
            } GBP`,
          ])
        : [["No bookings found", "", "", ""]],
  });

  y = pdf.lastAutoTable.finalY + 18;

  brandAccentText(pdf, "Payments and Adjustments", pageWidth / 2, y, 12);
  y += 8;

  const paymentRows =
    payments && payments.length
      ? payments.map((payment) => {
          const discount = isDiscountPayment(payment);
          const formattedPaymentDate = payment.paymentdate
            ? formatLocalDateOnly(new Date(payment.paymentdate))
            : "";
          const label = discount
            ? `Discount applied${formattedPaymentDate ? ` ${formattedPaymentDate}` : ""}`
            : `Payment received${formattedPaymentDate ? ` ${formattedPaymentDate}` : ""}`;
          return [label, `${discount ? "-£" : "£"} ${Number(payment.paymentamount).toFixed(2)} GBP`];
        })
      : [["No payments recorded", ""]];

  autoTable(pdf, {
    ...houseTableDefaults,
    startY: y,
    head: [["Description", "Amount"]],
    body: paymentRows,
    columnStyles: {
      0: { cellWidth: 340 },
      1: { cellWidth: "auto", halign: "right" },
    },
  });

  y = pdf.lastAutoTable.finalY + 18;

  brandAccentText(pdf, "Totals", pageWidth / 2, y, 12);
  y += 8;

  const totalsRows = [["Total", `£ ${(gross ? totalGrossCharge : totalNetCharge).toFixed(2)} GBP`]];

  if (paidTotalExcludingDiscounts === 0) {
    totalsRows.push(["Deposit due on receipt of invoice", `£ ${depositDue.toFixed(2)} GBP`]);
  }

  totalsRows.push([
    "Balance Due by 15th May",
    `£ ${(gross ? outstandingBalanceGross : outstandingBalanceNet).toFixed(2)} GBP`,
  ]);

  autoTable(pdf, {
    ...houseTableDefaults,
    startY: y,
    head: [["Summary", "Amount"]],
    body: totalsRows,
    columnStyles: {
      0: { cellWidth: 340, fontStyle: "bold" },
      1: { cellWidth: "auto", halign: "right" },
    },
  });

  drawFooter(pdf);

  const fullName =
    `${student.studentName || ""} ${student.studentSurname || ""}`.trim() ||
    String(student?.id || "");
  pdf.save(`${fullName}_${formattedPdfDate}.pdf`);
}
